import React from 'react';
import type { ComponentConfig } from '@puckeditor/core';
import styled from 'styled-components';

import { ColorRoleField } from '../fields/widgets/color-role-field';
import { SURFACE_COLOR_FIELD_AI_SCHEMA, SurfaceColorField } from '../fields/widgets/surface-color-field';
import { createSpacingFields } from '../fields/spacing-fields';
import { interpolateFromPuck } from '../interpolation';
import {
  BLOCK_WIDTHS,
  FONT_ROLES,
  HEADING_LINE_HEIGHT,
  HEADING_PRESETS,
  lookup,
  MAX_WIDTHS,
  NO_SPACING,
  resolveTextColor,
  resolveSurfaceColor,
  spacingToCss,
  SURFACE_COLOR_ROLES,
  TEXT_COLOR_ROLES,
} from '../fields/tokens';
import type { BlockWidthOption, BoxSpacing, FontRole, HeadingPreset, MaxWidthOption } from '../fields/tokens';

const HEADING_LEVELS = ['h1', 'h2', 'h3', 'h4', 'h5', 'h6'] as const;

type HeadingLevel = typeof HEADING_LEVELS[number];
type TextAlign = 'center' | 'left' | 'right';

/**
 * Size can track the semantic level instead of pinning a step. This is the default so
 * that changing Level visibly changes size, which is what people expect; overriding it
 * still allows a correct document outline at a size design asked for.
 */
const MATCH_LEVEL = 'match';

type HeadingSize = HeadingPreset | typeof MATCH_LEVEL;

export type HeadingProps = {
  text: string;
  /** Semantic tag. Drives visual size too, unless `preset` overrides it. */
  level: HeadingLevel;
  /** Visual size, or `match` to follow `level`. */
  preset: HeadingSize;
  font: FontRole;
  color: string;
  /** Either a surface role key (e.g. `'surface'`) or a custom hex (e.g. `'#ff0000'`). */
  background: string;
  align: TextAlign;
  width: BlockWidthOption;
  maxWidth: MaxWidthOption;
  padding: BoxSpacing;
};

/**
 * Per-instance editor choices arrive as `--cms-*` properties set inline, which win over
 * the `--dcm-*` theme defaults baked into the fallbacks below. That ordering is what lets
 * an untouched heading follow merchant branding while an explicitly styled one does not.
 */
const StyledHeading = styled.h2`
  margin: 0;
  background: var(--cms-heading-background, ${SURFACE_COLOR_ROLES.none});
  color: var(--cms-heading-color, ${TEXT_COLOR_ROLES.text});
  font-family: var(--cms-heading-font-family, ${FONT_ROLES.heading});
  font-size: var(--cms-heading-font-size, ${HEADING_PRESETS.h2.fontSize});
  font-weight: var(--cms-heading-font-weight, ${HEADING_PRESETS.h2.fontWeight});
  line-height: var(--cms-heading-line-height, ${HEADING_LINE_HEIGHT});
  text-align: var(--cms-heading-text-align, left);
  width: var(--cms-heading-width, ${BLOCK_WIDTHS.fill});
  max-width: var(--cms-heading-max-width, ${MAX_WIDTHS.none});
  padding: var(--cms-heading-padding, 0px);
`;

const SPACING_FIELDS = createSpacingFields();

export const headingConfig: ComponentConfig<HeadingProps> = {
  label: 'Heading',
  ai: {
    instructions:
      'A text heading element. Use for page titles, section titles, and content hierarchy. Always set meaningful text content. Set `level` for document structure (one h1 per page); visual size follows it by default, so only set `preset` to break that link deliberately.',
  },
  fields: {
    text: { type: 'textarea', label: 'Text' },
    level: {
      type: 'select',
      label: 'Level',
      options: HEADING_LEVELS.map((level) => ({ label: level.toUpperCase(), value: level })),
      ai: {
        schema: { type: 'string', enum: [...HEADING_LEVELS] },
        instructions:
          'HTML heading level, for accessibility and SEO only. Use h1 for the page title (once per page), h2 for sections, h3+ for subsections.',
      },
    },
    preset: {
      type: 'select',
      label: 'Size',
      options: [
        { label: 'Match level', value: MATCH_LEVEL },
        ...HEADING_LEVELS.map((level) => ({ label: `Heading ${level.slice(1)}`, value: level })),
      ],
      ai: {
        schema: { type: 'string', enum: [MATCH_LEVEL, ...HEADING_LEVELS] },
        instructions:
          "Visual size. Leave as 'match' so it follows `level`; override only when design needs a size that differs from the document outline.",
      },
    },
    font: {
      type: 'radio',
      label: 'Font',
      options: [
        { label: 'Heading', value: 'heading' },
        { label: 'Body', value: 'body' },
      ],
      ai: {
        schema: { type: 'string', enum: ['heading', 'body'] },
        instructions:
          "Which of the merchant's two configured fonts to use. Prefer 'heading' unless the text is deliberately styled as body copy.",
      },
    },
    background: {
      type: 'custom',
      label: 'Background',
      render: SurfaceColorField,
      ai: {
        schema: SURFACE_COLOR_FIELD_AI_SCHEMA,
        instructions:
          "Optional fill behind the heading. Set to a surface role ('none', 'surface', 'muted') so it follows merchant branding; use a hex value only when a specific brand color is required.",
      },
    },
    color: {
      type: 'custom',
      label: 'Text color',
      render: ColorRoleField,
      ai: {
        schema: {
          type: 'string',
          description:
            "Either a theme color role ('text', 'secondary', 'muted', 'inverted', 'link') or a hex value such as #121516. Prefer a role so the heading follows merchant branding.",
        },
      },
    },
    align: {
      type: 'radio',
      label: 'Alignment',
      options: [
        { label: 'Left', value: 'left' },
        { label: 'Center', value: 'center' },
        { label: 'Right', value: 'right' },
      ],
      ai: { schema: { type: 'string', enum: ['left', 'center', 'right'] } },
    },
    width: {
      type: 'radio',
      label: 'Width',
      options: [
        { label: 'Fit', value: 'fit' },
        { label: 'Fill', value: 'fill' },
      ],
      ai: { schema: { type: 'string', enum: ['fit', 'fill'] } },
    },
    maxWidth: {
      type: 'radio',
      label: 'Max width',
      options: [
        { label: 'Narrow', value: 'narrow' },
        { label: 'Normal', value: 'normal' },
        { label: 'None', value: 'none' },
      ],
      ai: { schema: { type: 'string', enum: ['narrow', 'normal', 'none'] } },
    },
    padding: {
      type: 'object',
      label: 'Padding',
      objectFields: SPACING_FIELDS,
    },
  },
  defaultProps: {
    text: 'Heading',
    level: 'h2',
    preset: MATCH_LEVEL,
    font: 'heading',
    color: 'text',
    background: 'none',
    align: 'left',
    width: 'fill',
    maxWidth: 'none',
    padding: NO_SPACING,
  },
  render: ({
    text = 'Heading',
    level = 'h2',
    preset = MATCH_LEVEL,
    font = 'heading',
    color = 'text',
    background = 'none',
    align = 'left',
    width = 'fill',
    maxWidth = 'none',
    padding = NO_SPACING,
    puck,
  }) => {
    const tag = HEADING_LEVELS.includes(level) ? level : 'h2';
    const sizeKey = preset === MATCH_LEVEL ? tag : preset;
    const sizing = lookup(HEADING_PRESETS, sizeKey, HEADING_PRESETS.h2);
    const resolvedText = interpolateFromPuck(text, puck);

    return (
      <StyledHeading
        as={tag}
        style={
          {
            '--cms-heading-background': resolveSurfaceColor(background),
            '--cms-heading-color': resolveTextColor(color),
            '--cms-heading-font-family': lookup(FONT_ROLES, font, FONT_ROLES.heading),
            '--cms-heading-font-size': sizing.fontSize,
            '--cms-heading-font-weight': sizing.fontWeight,
            '--cms-heading-text-align': align,
            '--cms-heading-width': lookup(BLOCK_WIDTHS, width, BLOCK_WIDTHS.fill),
            '--cms-heading-max-width': lookup(MAX_WIDTHS, maxWidth, MAX_WIDTHS.none),
            '--cms-heading-padding': spacingToCss(padding),
          } as React.CSSProperties
        }
      >
        {resolvedText}
      </StyledHeading>
    );
  },
};
